/**
 * Personal Running-Now Contracts
 *
 * Account-level Running Now DataTable: a user's own AI processes, org-agnostic.
 * Mirrors the org contract (./shadow-ai-running-now.ts) and its field enums,
 * with three deviations:
 *   - "User" column is the OS `processUser` (nullable), not the Kirin member;
 *   - the response adds `lastHeartbeat` for AgentStatus;
 *   - scope is the requesting user only.
 */
import { z } from 'zod';
import { toolApprovalQueryParamSchema, } from './shadow-ai-tool-approvals';
import { baseListQuerySchema, sortOrderSchema, } from './helpers';
import { ColumnDataTypes, createTableQuerySchema, FilterOperators, FiltersSchema, FilterValueSchema, TableResponseMetaSchema, } from './table';
import { runningNowListItemSchema, runningNowSortFieldSchema, RUNNING_NOW_SORTABLE_FIELDS, RUNNING_NOW_SEARCHABLE_FIELDS, RUNNING_NOW_OPERATOR_OVERRIDES, } from './shadow-ai-running-now';
/** Shared org field enums, re-exported so personal consumers import from one place — values are identical to org. */
export { RUNNING_NOW_SORTABLE_FIELDS, RUNNING_NOW_SEARCHABLE_FIELDS, RUNNING_NOW_OPERATOR_OVERRIDES, runningNowSortFieldSchema, };
/** Personal filterable fields — the "User" column filters by OS `processUser`, not the Kirin org member. */
export const PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    PROCESS_USER: 'processUser',
    PID: 'pid',
    PORT: 'port',
    STARTED_AT: 'startedAt',
};
/** Fields backed by a dynamic filter-options endpoint. pid/port (NUMBER) and startedAt (date) have none. */
export const PERSONAL_RUNNING_NOW_FILTER_OPTION_FIELDS = {
    NAME: 'name',
    CATEGORY: 'category',
    PROCESS_USER: 'processUser',
};
export const personalRunningNowFilterOptionFieldSchema = z.enum([
    PERSONAL_RUNNING_NOW_FILTER_OPTION_FIELDS.NAME,
    PERSONAL_RUNNING_NOW_FILTER_OPTION_FIELDS.CATEGORY,
    PERSONAL_RUNNING_NOW_FILTER_OPTION_FIELDS.PROCESS_USER,
]);
/** Org row, but the "User" column is the OS owner: non-empty string, or null when unreported. */
export const personalRunningNowListItemSchema = runningNowListItemSchema.extend({
    processUser: z.string().min(1).nullable(),
});
/** Column → data-type map consumed internally by the query schema. Not exported. */
const PERSONAL_RUNNING_NOW_FILTER_COLUMN_DATA_TYPES = {
    [PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.CATEGORY]: ColumnDataTypes.ENUM,
    [PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.PROCESS_USER]: ColumnDataTypes.ENUM,
    [PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.PID]: ColumnDataTypes.NUMBER,
    [PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.PORT]: ColumnDataTypes.NUMBER,
    [PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.STARTED_AT]: ColumnDataTypes.DATE,
};
/** Query for the personal filter-options endpoint (distinct values for one field). */
export const personalRunningNowFilterOptionsQuerySchema = z.object({
    field: personalRunningNowFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/** Personal filter options: plain distinct string values, with no org-member metadata. */
export const personalRunningNowFilterOptionsResponseSchema = z.object({
    field: personalRunningNowFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const personalRunningNowTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: runningNowSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    isRoot: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform(v => v === true || v === 'true')
        .optional(),
    // KIR-4864: Show filter (not_approved | approved | all) against the
    // user's personal approvals. Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
/**
 * Per-column operator overrides. Process (`name`) narrows to
 * includes/excludes/contains/not-contains plus the value-less operators — no
 * starts-with/ends-with; `startedAt` inherits the org date override.
 */
export const PERSONAL_RUNNING_NOW_OPERATOR_OVERRIDES = {
    ...RUNNING_NOW_OPERATOR_OVERRIDES,
    [PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.NAME]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.CONTAINS,
        FilterOperators.NOT_CONTAINS,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
export const personalRunningNowTableQuerySchema = createTableQuerySchema(personalRunningNowTableQueryBaseSchema, PERSONAL_RUNNING_NOW_FILTER_COLUMN_DATA_TYPES, PERSONAL_RUNNING_NOW_OPERATOR_OVERRIDES)
    .superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        // pid/port (NUMBER) are validated by the shared createTableQuerySchema range rules.
        if (filter.field === PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.STARTED_AT) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Filter values for startedAt field must be valid ISO 8601 datetime strings',
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
})
    .transform((data) => {
    // Keep pid/port positional empties — they are open-ended range bounds.
    return {
        page: data.page,
        limit: data.limit,
        search: data.search,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
        filters: data.filters,
        isRoot: data.isRoot,
        approval: data.approval,
    };
});
/** Query params for the personal running-now list endpoint, serialized from DataTable state. */
export const listPersonalRunningNowDataTableParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: runningNowSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: z.array(FilterValueSchema).optional(),
    isRoot: z.boolean().optional(),
    // KIR-4864: Show filter. Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
/**
 * Personal running-now table response: paginated rows plus `lastHeartbeat` —
 * the requesting user's latest agent heartbeat (nullable), which drives AgentStatus.
 */
export const personalRunningNowTableResponseSchema = z.object({
    data: z.array(personalRunningNowListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
    lastHeartbeat: z.string().datetime({ offset: true }).nullable(),
});
//# sourceMappingURL=account-shadow-ai-running-now.js.map